/**
 * Full SSR (treatment) entry for the market route. Mounted via
 * `dynamicWithExperiment` from `routes/_app.market.tsx` whenever the
 * global `SSR_DUAL_MODE_EXPERIMENT_NAME` experiment resolves to
 * `treatment`.
 *
 * Mirrors the previous inline `default` Component:
 * - Builds a route-scoped Apollo client **once** per route mount via
 *   `useRef` so React Router 7's hydrate-time `loaderData`
 *   re-deserialisation doesn't tear the cache down.
 * - Seeds the `ListGenerationModels` query keyed on the same
 *   `SSR_LIST_VARIABLES` that `<BaseModelListInfiniteScroll>`
 *   synthesises on first mount, so `cache-first` `useQuery` lands on
 *   the SSR-prefetched 24 model cards without a network round-trip.
 *
 * The CSR (control) entry lives in `_app.market.csr.tsx` and skips
 * the seed entirely — the live grid renders its skeleton until the
 * Apollo query resolves, equivalent to Stage 1 behaviour.
 */
import type { ApolloClient } from '@apollo/client'
import { ApolloProvider } from '@apollo/client/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { useRef } from 'react'
import { useLoaderData } from 'react-router'
import { Component as MarketPage } from 'src/pages/AppPage/MarketPage'
import type { loader } from 'src/routes/_app.market'
import {
  ListGenerationModelsDocument,
  type ListGenerationModelsQuery,
  type ListGenerationModelsQueryVariables,
} from 'src/services/apollo/generated/graphql'
import { createRouteApolloClient } from 'src/services/apollo/route-ssr-client'
import { useRequestContext } from 'src/utils/request-context'

// Mirrors the constant in `routes/_app.market.tsx` so the seed
// variables match exactly what `<BaseModelListInfiniteScroll>`
// synthesises on first mount with the default URL state. Drift here
// breaks the cache-first hit and produces a visible skeleton flash
// on hydrate.
const SSR_LIST_VARIABLES: ListGenerationModelsQueryVariables = {
  feed: 'trending',
  last: 24,
}

export default function MarketFullSSREntry() {
  const loaderData = useLoaderData<typeof loader>()
  const { queryClient } = useRequestContext()

  const clientRef = useRef<ApolloClient | null>(null)
  if (clientRef.current == null) {
    const seeds: Parameters<typeof createRouteApolloClient>[0] =
      loaderData.listConnection
        ? [
            {
              query: ListGenerationModelsDocument,
              variables: SSR_LIST_VARIABLES,
              data: {
                generationModels: loaderData.listConnection,
              } satisfies ListGenerationModelsQuery,
            },
          ]
        : []
    clientRef.current = createRouteApolloClient(seeds)
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ApolloProvider client={clientRef.current}>
        <MarketPage />
      </ApolloProvider>
    </QueryClientProvider>
  )
}
